// Регистр судов РМРС — зеркало реестра Российского морского регистра судоходства. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН МОЖЕТ И ЧЕГО НЕ МОЖЕТ, И ЭТО ВИДНО СРАЗУ.
// Может: найти судно по названию, IMO, регистровому номеру или позывному; сказать, есть ли
// оно у нас в CRM; показать, кто в порту приписки признан Регистром на радио-навигационное
// оборудование; показать, что мы сами на этом судне делали.
// НЕ может: сказать, что на судне установлено и что на нём одобрено, и когда ближайшее
// освидетельствование. Этого нет в данных: колонки last_survey/next_survey, тип, символ
// класса, год постройки и валовая пусты во ВСЕХ 4 186 строках (на 28.07.2026) — портал 55
// берёт их из файла в R2 («RMRS/ships.json»), которого в ядре нет. Поэтому здесь нет ни
// фильтра по типу судна, ни списка «что просрочено»: пустой фильтр читался бы как «ничего
// не просрочено».
//
// ПОИСК. В реестре названия ЛАТИНИЦЕЙ («RUSICH-5»), у нас в CRM кириллицей («Русич-5»).
// Быстрый поиск идёт с начала строки по индексу upper(name) — по-русски он не найдёт ничего.
// Поэтому рядом стоит «искать внутри строки»: он медленнее (полный проход), зато заглядывает
// и в названия НАШИХ судов с тем же IMO. Совпадение при этом всё равно устанавливается по IMO.
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useP55Refs, useСудаРС, useСудноРС } from '../api/p55'
import type { КарточкаСудна, СправочникиP55, СудноРС } from '../api/p55'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, plural, pluralN } from '../lib/format'
import { ПУТИ, РАЗМЕР_СТРАНИЦЫ, возрастДней } from '../lib/p55'
import type { ФильтрыP55 } from '../lib/p55'

export default function RsVessels() {
  const { imo } = useParams<{ imo: string }>()
  return imo ? <Карточка imo={imo} /> : <Список />
}

// ═══════════════════════════════ общее для трёх экранов раздела ═══════════════════════════════
// Свежесть, пометка охвата и пейджер одинаковы у судов, изделий и сервисников. Живут они здесь,
// а не в components/: это три экрана одного раздела, и заводить ради пятнадцати строк общий
// файл в чужой папке — значит спорить за него с другими разделами. Экспортируются, чтобы
// RsProducts и RsSuppliers не размножили три копии, которые потом разойдутся.

/** Свежесть: зеркало и сам обход реестра — это РАЗНЫЕ сроки, и второй куда важнее. */
export function Свежесть({ refs }: { refs: СправочникиP55 | undefined }) {
  if (!refs) return null
  const обход = возрастДней(refs.synced.scraped_vessels)
  const синк = возрастДней(refs.synced.vessels)
  return (
    <div className="hint">
      Зеркало обновлено {fmtDate(refs.synced.vessels, true)}
      {синк !== null ? ` (${синк === 0 ? 'сегодня' : `${синк} дн. назад`})` : ''}.
      {' '}Сам реестр обойдён скрапером {fmtDate(refs.synced.scraped_vessels)}
      {обход !== null ? ` — ${обход} дн. назад` : ''}: свежесть данных задаёт эта дата,
      а не время синка.
    </div>
  )
}

export function Пейджер({ start, total, next, busy, onStart }: {
  start: number
  total: number
  next: number | null
  busy: boolean
  onStart: (v: number) => void
}) {
  if (total <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0 || busy}
              onClick={() => onStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>← назад</button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, total)} из {total}
      </span>
      <button disabled={next === null || busy} onClick={() => onStart(next!)}>вперёд →</button>
    </div>
  )
}

/** Пометка BFF «показано не всё». У реестра сужать нечего, но соврать молчанием нельзя. */
export function Охват({ scope }: { scope?: { limited?: boolean; partial?: boolean; note?: string } }) {
  if (!scope?.limited) return null
  return (
    <div className="notice">
      <b>Показано не всё.</b> Ядро сузило выборку по вашей зоне ответственности
      {scope.note ? `: ${scope.note}` : ''}.
      {scope.partial
        ? ' Пометка «сужено неполно» означает, что часть строк не попала в ответ даже внутри'
          + ' вашей зоны — на эти числа опираться нельзя.'
        : ''}
    </div>
  )
}

// ═══════════════════════════════ список ═══════════════════════════════

function Список() {
  const [q, setQ] = useState('')
  const [deep, setDeep] = useState(false)
  const [flag, setFlag] = useState('')
  const [port, setPort] = useState('')
  const [mine, setMine] = useState('')
  const [sort, setSort] = useState('name')
  const [start, setStart] = useState(0)

  const refs = useP55Refs()
  const счёт = refs.data?.counts

  const фильтры = useMemo<ФильтрыP55>(() => ({
    q: q.trim() || undefined,
    deep: deep || undefined,
    flag: flag || undefined,
    port: port || undefined,
    mine: mine || undefined,
    sort,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, deep, flag, port, mine, sort, start])

  const запрос = useСудаРС(фильтры)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <div className="page-head">
        <h1>Суда в реестре</h1>
        <span className="muted">
          {данные ? `найдено: ${данные.total}` : ''}
          {счёт ? ` · в реестре: ${счёт.vessels}` : ''}
        </span>
      </div>

      <Охват scope={данные?._scope} />

      {/* Не мелкая оговорка внизу, а первое, что видит человек: он придёт сюда за сроками
          освидетельствования и типом судна, и должен сразу узнать, что этого в данных нет. */}
      <div className="notice">
        <b>В зеркале реестра только «паспортная» часть.</b> Есть IMO, регистровый номер,
        название, флаг, порт приписки и позывной. Тип судна, символ класса, год постройки,
        валовая вместимость, оборудование на борту и <b>сроки освидетельствования</b> в источнике
        (база реестра) отсутствуют — портал 55 берёт их из отдельного файла, которого в ядре
        нет. Поэтому фильтров «по типу судна» и «что просрочено» здесь нет: пустой фильтр
        читался бы как «ничего не просрочено».
        {refs.data?.empty_columns.vessels.length ? (
          <div className="hint" style={{ marginTop: 6 }}>
            Пусты во всех {счёт?.vessels}{' '}
            {plural(счёт?.vessels ?? 0, 'строке', 'строках', 'строках')}:{' '}
            {refs.data.empty_columns.vessels.join(', ')}.
          </div>
        ) : null}
      </div>

      {счёт ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="tiles">
            <div className="tile">
              <div className="t-label">Судов в реестре РС</div>
              <div className="t-sum num">{счёт.vessels}</div>
            </div>
            <div className="tile">
              <div className="t-label">Наших судов в реестре</div>
              <div className="t-sum num">{счёт.rs_vessels_ours}</div>
              <div className="t-label">из {счёт.our_vessels_with_imo} наших судов с IMO</div>
            </div>
            <div className="tile">
              <div className="t-label">Наших судов всего</div>
              <div className="t-sum num">{счёт.our_vessels}</div>
              <div className="t-label">без IMO — {счёт.our_vessels - счёт.our_vessels_with_imo},
                они не сверяются вовсе</div>
            </div>
            <div className="tile">
              <div className="t-label">IMO с дублями у нас</div>
              <div className="t-sum num">{счёт.our_imo_duplicated}</div>
              <div className="t-label">поэтому наших записей совпало {счёт.our_vessels_in_rs},
                а судов реестра — {счёт.rs_vessels_ours}</div>
            </div>
          </div>
          <div className="hint" style={{ marginTop: 10 }}>
            Сверка идёт <b>только по IMO</b>. По названию сверять нельзя: в реестре латиница,
            у нас кириллица.
          </div>
        </div>
      ) : null}

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="rs-vessels">
          <div className="f" style={{ flex: 2, minWidth: 260 }}>
            <label htmlFor="rvq">Название, IMO, регистровый номер или позывной</label>
            <input id="rvq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: RUSICH, 9353046, 040224 или UBHY" />
          </div>

          <div className="f" style={{ minWidth: 210 }}>
            <label htmlFor="rvdeep">Как искать</label>
            <select id="rvdeep" value={deep ? '1' : ''}
                    onChange={(e) => сСброса(setDeep)(e.target.value === '1')}>
              <option value="">с начала строки (быстро)</option>
              <option value="1">внутри строки и по нашим названиям</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="rvflag">Флаг</label>
            <select id="rvflag" value={flag} onChange={(e) => сСброса(setFlag)(e.target.value)}>
              <option value="">любой</option>
              {(refs.data?.vessel_flag ?? []).map((f) => (
                <option key={f.k} value={f.k}>{f.k || '(не указан)'} ({f.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="rvport">Порт приписки</label>
            <select id="rvport" value={port} onChange={(e) => сСброса(setPort)(e.target.value)}>
              <option value="">любой</option>
              {(refs.data?.vessel_port ?? []).map((p) => (
                <option key={p.k} value={p.k}>{p.k || '(не указан)'} ({p.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="rvmine">Есть ли у нас</label>
            <select id="rvmine" value={mine} onChange={(e) => сСброса(setMine)(e.target.value)}>
              <option value="">все суда реестра</option>
              <option value="1">только наши (есть в CRM)</option>
              <option value="0">только те, которых у нас нет</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 160 }}>
            <label htmlFor="rvsort">Сортировка</label>
            <select id="rvsort" value={sort} onChange={(e) => сСброса(setSort)(e.target.value)}>
              <option value="name">по названию</option>
              <option value="imo">по IMO</option>
              <option value="rs">по регистровому номеру</option>
              <option value="flag">по флагу</option>
              <option value="port">по порту приписки</option>
            </select>
          </div>
        </FilterBar>

        {q.trim() && !deep && (
          <div className="hint" style={{ marginTop: 10 }}>
            Ищем <b>с начала строки</b>: «RUS» найдёт RUSICH, но «Русич» не найдёт ничего —
            в реестре названия латиницей. Переключите «как искать» на «внутри строки», чтобы
            поиск заглянул и в названия наших судов.
          </div>
        )}
        {deep && (
          <div className="hint" style={{ marginTop: 10 }}>
            Поиск внутри строки идёт полным проходом по реестру и по нашему флоту — это
            десятки миллисекунд вместо единиц. Зато находит по русскому названию.
          </div>
        )}
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Название в реестре</th>
                  <th className="nowrap">IMO</th>
                  <th className="nowrap">РС №</th>
                  <th className="nowrap">Позывной</th>
                  <th className="nowrap">Флаг</th>
                  <th>Порт приписки</th>
                  <th>Наше судно в CRM</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((v) => <Строка key={v.imo} v={v} />)}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty">
                        <div className="empty-title">Под эти условия судов в реестре нет</div>
                        <div className="empty-hint">
                          {данные.search_mode === 'с начала строки'
                            ? 'Искали с начала строки. Русское название так не найдётся —'
                              + ' переключите «как искать» на «внутри строки».'
                            : 'Реестр РС содержит только суда под классом Регистра —'
                              + ' судна другого классификационного общества в нём не будет.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Пейджер start={данные?.start ?? 0} total={данные?.total ?? 0}
                   next={данные?.next ?? null} busy={запрос.isFetching}
                   onStart={setStart} />
          <Свежесть refs={refs.data} />
        </div>
      </QueryState>
    </>
  )
}

function Строка({ v }: { v: СудноРС }) {
  return (
    <tr>
      <td data-label="Название в реестре">
        <Link to={ПУТИ.судно(v.imo)}>{fmtText(v.name)}</Link>
      </td>
      <td className="mono nowrap" data-label="IMO">{fmtText(v.imo)}</td>
      <td className="mono nowrap" data-label="РС №">{fmtText(v.rs_number)}</td>
      <td className="mono nowrap" data-label="Позывной">{fmtText(v.call_sign)}</td>
      <td className="nowrap" data-label="Флаг">{fmtText(v.flag)}</td>
      <td data-label="Порт приписки">{fmtText(v.port_of_registry)}</td>
      <td data-label="Наше судно в CRM">
        {v.ours?.bitrix_id
          ? (
            <>
              <EntityLink kind="vessel" id={v.ours.bitrix_id} title={v.ours.name} />
              {(v.ours.count ?? 1) > 1 && (
                <div className="hint">
                  у нас {pluralN(v.ours.count ?? 1, 'запись', 'записи', 'записей')}
                  {' '}с этим IMO
                </div>
              )}
            </>
          )
          : <span className="muted">нет в CRM</span>}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ imo }: { imo: string }) {
  const запрос = useСудноРС(imo)
  const refs = useP55Refs()
  const v = запрос.data

  return (
    <>
      <div className="page-head">
        <h1>{v?.name || `Судно IMO ${imo}`}</h1>
        <div className="spacer" />
        <Link to={ПУТИ.суда}>← к реестру</Link>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {v && (
          <>
            <div className="grid-2">
              <div>
                <div className="card">
                  <h2>Запись Регистра</h2>
                  <dl className="dl">
                    <dt>IMO</dt><dd className="mono">{fmtText(v.imo)}</dd>
                    <dt>Регистровый номер</dt><dd className="mono">{fmtText(v.rs_number)}</dd>
                    <dt>Название</dt><dd>{fmtText(v.name)}</dd>
                    <dt>Флаг</dt><dd>{fmtText(v.flag)}</dd>
                    <dt>Порт приписки</dt><dd>{fmtText(v.port_of_registry)}</dd>
                    <dt>Позывной</dt><dd className="mono">{fmtText(v.call_sign)}</dd>
                    <dt>В реестре при последнем обходе</dt>
                    <dd>{v.in_rs ? 'да' : 'нет — при обходе судно в реестре не найдено'}</dd>
                    <dt>Карточка на сайте РС</dt>
                    <dd>
                      {v.source_url
                        ? <a href={v.source_url} target="_blank" rel="noreferrer">
                            открыть в реестре РМРС ↗
                          </a>
                        : <span className="muted">ссылки в данных нет</span>}
                    </dd>
                    <dt>Впервые увидено / обойдено</dt>
                    <dd className="muted">
                      {fmtDate(v.first_seen)} · {fmtDate(v.scraped_at)}
                    </dd>
                  </dl>
                  <div className="hint" style={{ marginTop: 8 }}>
                    Источник — обход сайта Регистра скрапером, а не сам Регистр. Перед тем как
                    опираться на запись, откройте карточку РС по ссылке.
                  </div>
                </div>

                <ЧегоНет v={v} refs={refs.data} />
                <СервисникиВПорту v={v} />
              </div>

              <div>
                <НашиЗаписи v={v} />
                <НашиРаботы v={v} />
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  )
}

function ЧегоНет({ v, refs }: { v: КарточкаСудна; refs: СправочникиP55 | undefined }) {
  const пусто = refs?.empty_columns.vessels ?? []
  // Показываем не «прочерк в поле», а список того, чего нет вовсе. Прочерк у «ближайшего
  // освидетельствования» читался бы как «сроков нет», то есть «всё в порядке».
  const подписи: Record<string, string> = {
    vessel_type: 'тип судна',
    class_notation: 'символ класса',
    owner: 'судовладелец',
    build_year: 'год постройки',
    gross_tonnage: 'валовая вместимость',
    last_survey: 'последнее освидетельствование',
    next_survey: 'ближайшее освидетельствование',
    survey_postpone: 'крайний срок окна освидетельствования',
    surveys: 'разбивка по видам сюрвеев',
    builder: 'построено на',
  }
  return (
    <div className="card">
      <h2>Чего про это судно в данных нет</h2>
      {пусто.length === 0 ? (
        <div className="empty">
          <div className="empty-title">
            Все колонки реестра заполнены хотя бы у части судов — смотрите запись Регистра выше.
          </div>
        </div>
      ) : (
        <>
          <ul className="plain-list">
            {пусто.map((к) => (
              <li key={к}>
                {подписи[к] ?? к} <span className="muted mono">({к})</span>
              </li>
            ))}
          </ul>
          <div className="hint" style={{ marginTop: 8 }}>
            Эти колонки в зеркале есть, но пусты <b>у всех судов</b>: скрапер их не наполняет.
            Сроки освидетельствования и состав оборудования по этому судну искать надо в самом
            реестре РС (ссылка выше) или в наших актах.
          </div>
        </>
      )}
      <div className="hint">
        {v.data_notes.no_equipment_list
          ?? 'Состава оборудования судна в реестре РС нет в принципе — вопрос «что на нём'
            + ' одобрено» решается не реестром, а нашими актами и верификациями по тому же IMO.'}
      </div>
    </div>
  )
}

function НашиЗаписи({ v }: { v: КарточкаСудна }) {
  return (
    <div className="card">
      <h2>Наше судно</h2>
      {v.our_vessels.length === 0 ? (
        <div className="empty">
          <div className="empty-title">
            Судна с IMO <span className="mono">{v.imo}</span> у нас в CRM нет.
          </div>
          <div className="empty-hint">
            Это значит, что мы с ним не работали (или оно заведено без IMO — тогда сверить нечем).
          </div>
        </div>
      ) : (
        <>
          <ul className="plain-list">
            {v.our_vessels.map((s) => (
              <li key={s.bitrix_id}>
                <EntityLink kind="vessel" id={s.bitrix_id} title={s.name} />
                <div className="hint">
                  {fmtText(s.flag)} · порт {fmtText(s.home_port)} ·{' '}
                  <span className="mono">#{s.bitrix_id}</span>
                </div>
              </li>
            ))}
          </ul>
          {v.our_vessels.length > 1 && (
            <div className="notice" style={{ marginTop: 10, marginBottom: 0 }}>
              <b>У нас это судно заведено{' '}
              {pluralN(v.our_vessels.length, 'раз', 'раза', 'раз')}.</b> Один IMO — несколько
              контактов Bitrix. Раздел показывает их все и не выбирает «правильный»: выбор
              наугад однажды увёл бы сделку на дубль.
            </div>
          )}
          <div className="hint" style={{ marginTop: 8 }}>
            Совпадение установлено по IMO. Названия могут расходиться — в реестре
            «{fmtText(v.name)}», у нас «{fmtText(v.our_vessels[0].name)}»: это транслитерация,
            а не разные суда.
          </div>
        </>
      )}
    </div>
  )
}

function НашиРаботы({ v }: { v: КарточкаСудна }) {
  const bx = v.our_vessels[0]?.bitrix_id
  return (
    <div className="card">
      <h2>Что мы делали на этом судне</h2>
      <div className="tiles">
        <div className="tile">
          <div className="t-label">Актов на оборудование</div>
          <div className="t-sum num">{v.our_work.equip_acts}</div>
        </div>
        <div className="tile">
          <div className="t-label">Верификаций</div>
          <div className="t-sum num">{v.our_work.verifications}</div>
        </div>
      </div>
      {bx ? (
        <div style={{ marginTop: 10 }}>
          <Link to={`/equip-acts?vessel_bx=${bx}`}>акты по этому судну</Link>
          {' · '}
          <Link to={`/verifications?vessel_bx=${bx}`}>верификации</Link>
        </div>
      ) : (
        <div className="hint" style={{ marginTop: 10 }}>
          Ссылок нет: судна с этим IMO у нас в CRM не найдено, а разделы актов и верификаций
          ищут по нашей карточке судна.
        </div>
      )}
      <div className="hint" style={{ marginTop: 8 }}>
        Считается по IMO в зеркале портала 73. Это единственный ответ на «что на судне
        стоит», который есть в данных: реестр РС состава оборудования не содержит.
      </div>
    </div>
  )
}

function СервисникиВПорту({ v }: { v: КарточкаСудна }) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Кто вправе обслуживать в порту приписки</h2>
        <div className="spacer" />
        <span className="muted">{v.suppliers_in_port.length ? `компаний: ${v.suppliers_in_port.length}` : ''}</span>
      </div>

      {!v.port_city ? (
        <div className="notice" style={{ marginBottom: 0 }}>
          Порт приписки «{fmtText(v.port_of_registry)}» не сопоставлен с городом из реестра
          сервисников (в реестре судов порты записаны по-английски, в реестре сервисников
          города — по-русски). <b>Список пуст не потому, что признанных сервисников там нет</b> —
          сопоставления нет у нас. Ищите город в разделе «Признанные сервисники РС».
        </div>
      ) : v.suppliers_in_port.length === 0 ? (
        <div className="empty">
          <div className="empty-title">В городе {v.port_city} признанных РС сервисников по
            радио-навигации в реестре нет</div>
          <div className="empty-hint">
            Это не отсутствие данных, а факт реестра: смотрите ближайший порт в разделе
            «Признанные сервисники РС».
          </div>
        </div>
      ) : (
        <>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Компания</th>
                  <th className="nowrap">Областей</th>
                  <th className="nowrap">Поручителей</th>
                  <th className="nowrap">Признание до</th>
                  <th className="nowrap">Реестр РС</th>
                </tr>
              </thead>
              <tbody>
                {v.suppliers_in_port.map((s) => (
                  <tr key={s.company}>
                    <td data-label="Компания">
                      <Link to={ПУТИ.сервисник(s.company)}>{s.company}</Link>
                      {s.is_ours && <> <span className="badge won">это мы</span></>}
                      {!s.any_valid && <> <span className="badge lose">срок истёк</span></>}
                    </td>
                    <td className="num" data-label="Областей">{s.scopes}</td>
                    <td className="num" data-label="Поручителей">{s.authorizers}</td>
                    <td className="nowrap" data-label="Признание до">{fmtDate(s.valid_to_last)}</td>
                    <td className="nowrap" data-label="Реестр РС">
                      {s.doc_url
                        ? <a href={s.doc_url} target="_blank" rel="noreferrer">открыть ↗</a>
                        : <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Это <b>выборка по городу</b> «{v.port_city}», а не список тех, кто обслуживает
            именно это судно: связи «сервисник ↔ судно» в реестре РС не существует. Отобраны
            компании, чья область признания касается радио- и навигационного оборудования,
            ГМССБ, АРБ КОСПАС-САРСАТ или РДР.
          </div>
        </>
      )}
    </div>
  )
}
